/*
 * Copyright (c) Dito Technologies
 */

import {
    Action,
    NoteFinderUserConfirmationAddErrorPageIdAction,
    NoteFinderUserConfirmationAddLoadingPageIdAction,
    NoteFinderUserConfirmationAddSuccessPageIdAction,
    State
} from "ui/feature/root/FormReducer";
import React, {Dispatch} from "react";
import classnames from "classnames";
import styles from "./NoteFinderPageTools.module.scss";
import Button from "ui/kit/buttons/Button";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {faCheck, faUpload} from "@fortawesome/free-solid-svg-icons";
import NoteFinderUserConfirmation from "mutation/NoteFinderUserConfirmation";
import {
    NoteFinderPageTools_sheet$data
} from "ui/feature/root/note-select/__generated__/NoteFinderPageTools_sheet.graphql";

interface Props {
    className?: string;
    state: State;
    dispatch: Dispatch<Action>;
    sheet: NoteFinderPageTools_sheet$data;
}

const NoteFinderPageTools: React.FC<Props> = ({className, state, sheet, dispatch}) => {

    const markCompleteState: "loading" | "error" | "not_started" | "success" = React.useMemo(() => {
        console.log("note finder", state.noteFinderUserConfirmationLoadingPageIds, state.noteFinderUserConfirmationSuccessPageIds, state.noteFinderUserConfirmationErrorPageIds);
        if (state.noteFinderUserConfirmationLoadingPageIds.includes(sheet.id)) {
            return "loading";
        } else if (state.noteFinderUserConfirmationSuccessPageIds.includes(sheet.id)) {
            return "success";
        } else if (state.noteFinderUserConfirmationErrorPageIds.includes(sheet.id)) {
            return "error"
        }
        return "not_started";
    }, [state, sheet.id])

    const buttonDisabled: boolean = React.useMemo(() => {
        switch (markCompleteState) {
            case "success":
            case "loading":
                return true;
            default:
                return false;
        }
    }, [markCompleteState]);

    const markCompleteButtonTitle = React.useMemo(() => {
        switch (markCompleteState) {
            case "success":
                return "Done!"
            case "loading":
                return "Loading..."
            case "error":
                return "Error, try again"
            case "not_started":
                return "Mark Complete"
        }
    }, [markCompleteState]);

    const markCompleteButtonIcon = React.useMemo(() => {
        switch (markCompleteState) {
            case "success":
                return faCheck;
            default:
                return faUpload
        }
    }, [markCompleteState]);

    const onSubmit = React.useCallback(async () => {
        const parseSteps = sheet.note_finder_user_confirmation_steps;
        if (!parseSteps) {
            throw new Error("should have parse steps");
        }
        const parseStep = parseSteps[0];
        const action: NoteFinderUserConfirmationAddLoadingPageIdAction = {
            type: "note_finder_user_confirmation_add_loading_page_id",
            page_id: sheet.id
        };
        dispatch(action);
        try {
            await NoteFinderUserConfirmation(parseStep.id, sheet.id);
            const action: NoteFinderUserConfirmationAddSuccessPageIdAction = {
                type: "note_finder_user_confirmation_add_success_page_id",
                page_id: sheet.id
            };
            dispatch(action);
        } catch (e) {
            const action: NoteFinderUserConfirmationAddErrorPageIdAction = {
                type: "note_finder_user_confirmation_add_error_page_id",
                page_id: sheet.id
            };
            dispatch(action);
        }
    }, [dispatch, sheet.id, sheet.note_finder_user_confirmation_steps]);

    const isLoading: boolean = React.useMemo(() => {
        if (!sheet.note_combine_steps) {
            return true;
        }
        const step = sheet.note_combine_steps[0];
        return !step.data;
    }, [sheet]);

    if (isLoading) {
        // TODO (dito) make sure that this all works!
        return null;
    }

    return (
        <div className={classnames(classnames(styles.container, className))}>
            <div/>
            <div className={classnames(styles.buttonWrapper)}>
                <Button disabled={buttonDisabled} title={markCompleteButtonTitle} icon={markCompleteButtonIcon}
                        size={"small"} onClick={onSubmit}/>
            </div>
        </div>
    )
}

export default createFragmentContainer(NoteFinderPageTools, {
    sheet: graphql`
    fragment NoteFinderPageTools_sheet on MusicSheet {
        id
        note_combine_steps: steps(step_type: NOTE_DATA_SOURCE_COMBINE) {
            id
            status
            data {
                ... on MusicParseStepDataNoteDataSourceCombine {
                    notes {
                        note_id
                        ...NoteFinderPageOverlayNote_Note
                    }
                }
            }
        }
        note_finder_user_confirmation_steps: steps(step_type: NOTE_USER_CONFIRMATION) {
            id
            status
        }
    }
    `
});

